import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "./horizonTable.css";

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

const gridCols =
  "minmax(140px,1fr) minmax(95px,0.8fr) minmax(110px,0.8fr) minmax(85px,0.6fr) minmax(85px,0.6fr) minmax(120px,0.8fr) minmax(100px,0.7fr) minmax(200px,1.2fr) minmax(110px,0.8fr)";
const cellEllipsis = { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const tableMinWidth = "1100px";

function formatDate(d) {
  if (!d) return "—";
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  if (typeof d === "string") {
    const m = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      const year = Number(m[1]);
      const month = Number(m[2]);
      const day = Number(m[3]);
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        return `${String(day).padStart(2, "0")} ${monthNames[month - 1]} ${year}`;
      }
    }
  }
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return "—";
  const day = date.getUTCDate();
  const month = date.getUTCMonth();
  const year = date.getUTCFullYear();
  return `${String(day).padStart(2, "0")} ${monthNames[month]} ${year}`;
}

function formatTimeOnly(val) {
  if (val == null || val === "") return "—";
  const s = String(val).trim();
  if (/^\d{4}-\d{2}-\d{2}T\d/.test(s)) {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
      const h = d.getHours();
      const m = d.getMinutes();
      const sec = d.getSeconds();
      return sec
        ? `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
        : `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    }
  }
  return s || "—";
}

function formatMinutesAsHoursMinutes(value) {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return "—";
  const mins = Math.max(0, Math.round(num));
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  if (hours === 0) return `${rem} min`;
  if (rem === 0) return `${hours} hr`;
  return `${hours} hr ${rem} min`;
}

function findEntryInSheets(sheets, entryId, sheetId) {
  const list = Array.isArray(sheets) ? sheets : [];
  const targets = sheetId ? list.filter((s) => String(s._id) === String(sheetId)) : list;
  for (const sheet of targets) {
    const entries = Array.isArray(sheet.entries) ? sheet.entries : [];
    const entry = entries.find((e) => String(e._id) === String(entryId));
    if (entry) {
      return {
        sheet,
        entry,
        siteName: sheet.siteId?.siteName || sheet.siteId || "—",
        onCallEntries: Array.isArray(entry.onCallEntries) ? entry.onCallEntries : [],
        onCallCount: entry.onCallCount || 0,
        date: entry.date,
      };
    }
  }
  return null;
}

function FatigueWorkSheetOnCallPage() {
  const { entryId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const navState = location.state || {};

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [siteName, setSiteName] = useState(navState.siteName || "—");
  const [workerName, setWorkerName] = useState(navState.workerName || "");
  const [rowDate, setRowDate] = useState(navState.date || null);
  const [onCallEntries, setOnCallEntries] = useState(navState.onCallEntries || []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (navState.onCallEntries?.length) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(getBaseUrl() + "/api/fatigue-work-sheets", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.message || "Failed to load work sheets");
        }
        const found = findEntryInSheets(data.data, entryId, navState.sheetId);
        if (cancelled) return;
        if (!found) {
          setError("Worksheet entry not found");
          setOnCallEntries([]);
          return;
        }
        setSiteName(found.siteName);
        setRowDate(found.date);
        setOnCallEntries(found.onCallEntries);
        const wid = found.entry.workerId;
        if (wid && typeof wid === "object") {
          const name = [wid.firstName, wid.lastName].filter(Boolean).join(" ").trim();
          if (name) setWorkerName(name);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load on-call entries");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [entryId, navState.sheetId, navState.onCallEntries]);

  const goBack = () => {
    const search = navState.returnSearch || "";
    navigate(`/admin/fatigue/work-sheets${search}`);
  };

  const siteTitle =
    (siteName || "—") + (workerName && workerName !== "—" ? ` (${workerName})` : "");

  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: "20px",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
        padding: "24px",
        marginBottom: "24px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "20px", flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={goBack}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 14px",
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
            background: "#fff",
            color: "#8b1634",
            fontSize: "14px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          <i className="fa fa-arrow-left" />
          Back to Work Sheets
        </button>
        <div>
          <h2 style={{ fontSize: "22px", fontWeight: "700", color: "#1a202c", margin: 0 }}>
            On-Call Emergency Entries
          </h2>
          <p style={{ margin: "6px 0 0", color: "#718096", fontSize: "14px" }}>
            {siteTitle}
            {rowDate ? ` · ${formatDate(rowDate)}` : ""}
            {onCallEntries.length ? ` · ${onCallEntries.length} callout${onCallEntries.length === 1 ? "" : "s"}` : ""}
          </p>
        </div>
      </div>

      <div style={{ overflowX: "auto", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: gridCols,
            minWidth: tableMinWidth,
            width: "fit-content",
            background: "#e8f4fc",
            padding: "12px 16px",
            borderBottom: "1px solid #e2e8f0",
            gap: "16px",
            alignItems: "center",
          }}
        >
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Site</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Date</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Time of Callout</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>On Duty</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Off Duty</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Commuting Time</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Duty Length</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Callout Reason</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Created Date</div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#8b1634", fontWeight: "600" }}>
            Loading on-call entries...
          </div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#e53e3e" }}>{error}</div>
        ) : onCallEntries.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#718096" }}>
            No on-call emergency entries for this day.
          </div>
        ) : (
          onCallEntries.map((onCall, idx) => {
            const onCallReasonTitle = onCall?.calloutReason ? onCall.calloutReason : "";
            const updatedDate = onCall.createdAt || rowDate;
            const rowBg = "rgba(139, 22, 52, 0.04)";
            const rowHoverBg = "rgba(139, 22, 52, 0.08)";
            const rowSiteTitle = siteTitle + " — On-Call entry" + (onCallReasonTitle ? ` — ${onCallReasonTitle}` : "");

            return (
              <div
                key={onCall.id || `oncall-${idx}`}
                title={onCallReasonTitle || "On-call emergency entry"}
                style={{
                  display: "grid",
                  gridTemplateColumns: gridCols,
                  minWidth: tableMinWidth,
                  width: "fit-content",
                  padding: "16px",
                  borderBottom: "1px solid #f1f5f9",
                  gap: "16px",
                  alignItems: "center",
                  background: rowBg,
                  transition: "background-color 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = rowHoverBg;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = rowBg;
                }}
              >
                <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }} title={rowSiteTitle}>
                  {siteName || "—"}
                  {workerName && workerName !== "—" ? ` (${workerName})` : ""}
                </div>
                <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }}>{formatDate(rowDate)}</div>
                <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }} title={formatTimeOnly(onCall.timeOfCallout)}>
                  {formatTimeOnly(onCall.timeOfCallout)}
                </div>
                <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }} title={formatTimeOnly(onCall.onDuty)}>
                  {formatTimeOnly(onCall.onDuty)}
                </div>
                <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }} title={formatTimeOnly(onCall.offDuty)}>
                  {formatTimeOnly(onCall.offDuty)}
                </div>
                <div
                  style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }}
                  title={onCall.commutingTime ? `${onCall.commutingTime} min` : ""}
                >
                  {onCall.commutingTime ? formatMinutesAsHoursMinutes(onCall.commutingTime) : "—"}
                </div>
                <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }}>{onCall.dutyLength || "—"}</div>
                <div
                  style={{ color: "#2d3748", fontSize: "14px", whiteSpace: "normal", lineHeight: 1.3 }}
                  title={onCallReasonTitle}
                >
                  {onCall.calloutReason || "—"}
                </div>
                <div style={{ color: "#2d3748", fontSize: "14px", ...cellEllipsis }}>{formatDate(updatedDate)}</div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default FatigueWorkSheetOnCallPage;
